"use client";
import { useAppPreferences } from "@/components/AccentColorProvider";
import { getDeviceContextSnapshot } from "@/lib/deviceContext";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { fetchAllProjects } from "@/lib/projects";
import {
  buildAgentRunPayload,
  cancelAgentRun,
  createAgentRun,
  createAgentSession,
  listAgentSessions,
  loadAgentSessionMessages,
  openAgentRunEvents,
  type AgentModelMode,
  type AgentSessionListItem,
  type AgentSessionMessage,
} from "@/lib/agentRunTransport";
import { getAgentPath, getAgentSessionHref } from "@/lib/agentHub";
import { resolveRiskAssessmentRouteSyncAction } from "@/components/risk-assessment/riskAssessmentRouteSync";
import { APP_RESUME_EVENT, APP_SUSPEND_EVENT } from "@/lib/appLifecycle";
import {
  extractSearchWorkspaceAssistantText,
  type SearchWorkspaceConfig,
} from "./searchWorkspaceContract";

export type SearchWorkspaceState =
  "idle" | "loading" | "generating" | "ready" | "failed" | "cancelled";

export type SearchRunStageStatus = "pending" | "running" | "complete" | "error";

export type SearchRunAction = {
  id: string;
  toolName: string;
  title: string;
  status: SearchRunStageStatus;
  startedAt?: number;
  durationMs?: number;
  inputPreview?: string;
  resultPreview?: string;
};

export type SearchRunStage = {
  id: "verify" | "search" | "report";
  title: string;
  status: SearchRunStageStatus;
  startedAt?: number;
  durationMs?: number;
  summary?: string;
  actions?: SearchRunAction[];
};

export type SearchWorkspaceProject = {
  id: string;
  name: string;
};

export type SearchWorkspaceArtifact = {
  key: string;
  messageId?: string;
  messageIndex: number;
  title: string;
  xmlContent: string;
  fullContent: string;
};

export type SearchWorkspaceSubmission = {
  prompt: string;
  projectId?: string | null;
  modelMode: AgentModelMode;
};

const SESSION_BATCH_SIZE = 50;
// Core의 세션 surface는 'chat' | 'risk_assessment_workspace' 두 값뿐이다
// (그 외 값은 'chat'으로 강제). 전용 워크스페이스 세션을 홈 채팅의 최근
// 목록에서 제외하려면 위험성평가와 같은 workspace surface를 재사용해야 하며,
// 각 목록은 agent_id 필터로 서로를 구분한다.
const SESSION_SURFACE = "risk_assessment_workspace" as const;

const apiUrl = () => process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

const stripXml = (value: string) =>
  value
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const cleanSummary = (value: unknown, max = 150) => {
  if (typeof value !== "string") return undefined;
  const text = stripXml(value);
  return text
    ? `${text.slice(0, max)}${text.length > max ? "…" : ""}`
    : undefined;
};

const previewToolValue = (value: unknown, max = 400) => {
  if (value == null) return undefined;
  if (typeof value === "string") return cleanSummary(value, max);
  try {
    const record = value as Record<string, unknown>;
    const entries = Object.entries(record)
      .filter(([, item]) => item != null && item !== "")
      .map(
        ([key, item]) =>
          `${key}: ${typeof item === "string" ? item : JSON.stringify(item)}`,
      );
    if (entries.length > 0) return cleanSummary(entries.join(" · "), max);
    return cleanSummary(JSON.stringify(value), max);
  } catch {
    return undefined;
  }
};

const xmlText = (tag: string, source: string) => {
  const match = source.match(
    new RegExp(`<${tag}(?:\\s+[^>]*)?>([\\s\\S]*?)(?:<\\/${tag}>|$)`, "i"),
  );
  return (
    match?.[1]
      ?.replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim() ?? ""
  );
};

export const extractSearchWorkspaceArtifacts = (
  messages: AgentSessionMessage[],
  config: SearchWorkspaceConfig,
): SearchWorkspaceArtifact[] => {
  const artifacts: SearchWorkspaceArtifact[] = [];
  const pattern = new RegExp(
    `<${config.artifactTag}(?:\\s+[^>]*)?>([\\s\\S]*?)(?:<\\/${config.artifactTag}>|$)`,
    "gi",
  );
  messages.forEach((message, messageIndex) => {
    if (message.role !== "assistant" || typeof message.content !== "string")
      return;
    const matches = [...message.content.matchAll(pattern)];
    matches.forEach((match, artifactIndex) => {
      const xmlContent = match[1] ?? "";
      artifacts.push({
        key: `${messageIndex}:${config.artifactTag}:${artifactIndex}`,
        messageId: String(message.messageId ?? message.id ?? "") || undefined,
        messageIndex,
        title: config.artifactTitle(xmlContent),
        xmlContent,
        fullContent: message.content,
      });
    });
  });
  return artifacts;
};

export function useSearchWorkspaceRunController({
  config,
  languageLabel,
}: {
  config: SearchWorkspaceConfig;
  languageLabel: string;
}) {
  const { reasoningStyle } = useAppPreferences();
  const searchParams = useSearchParams();
  const routeSessionId = searchParams.get("session");
  const [state, setState] = useState<SearchWorkspaceState>(
    routeSessionId ? "loading" : "idle",
  );
  const [sessionId, setSessionId] = useState<string | null>(routeSessionId);
  const [runId, setRunId] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<SearchWorkspaceArtifact[]>([]);
  const [activeArtifactKey, setActiveArtifactKey] = useState<string | null>(
    null,
  );
  const [streamedAssistantContent, setStreamedAssistantContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [needsInput, setNeedsInput] = useState(false);
  const [projects, setProjects] = useState<SearchWorkspaceProject[]>([]);
  const [sessions, setSessions] = useState<AgentSessionListItem[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsInitialized, setSessionsInitialized] = useState(false);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const sessionPageRef = useRef(1);
  const lastSequenceRef = useRef(0);
  const streamAbortRef = useRef<AbortController | null>(null);
  const streamLifecycleSuspendedRef = useRef(false);
  const runContentRef = useRef("");
  const activeSessionIdRef = useRef<string | null>(routeSessionId);
  const hasSyncedInitialRouteRef = useRef(false);

  const initialStages = useMemo<SearchRunStage[]>(
    () => [
      { id: "verify", title: "검색 조건 확인", status: "pending" },
      { id: "search", title: config.searchStageTitle, status: "pending" },
      { id: "report", title: config.reportStageTitle, status: "pending" },
    ],
    [config.reportStageTitle, config.searchStageTitle],
  );
  const [stages, setStages] = useState<SearchRunStage[]>(initialStages);

  const toolTitle = useCallback(
    (toolName: string) =>
      config.toolLabels[toolName] ??
      stripXml(toolName.replace(/Tool$/, "").replace(/[-_]/g, " ")) ??
      "검색 작업",
    [config.toolLabels],
  );

  const clearRunOutput = useCallback(() => {
    runContentRef.current = "";
    setArtifacts([]);
    setActiveArtifactKey(null);
    setStreamedAssistantContent("");
  }, []);

  const activeArtifact = useMemo(
    () =>
      artifacts.find((artifact) => artifact.key === activeArtifactKey) ??
      artifacts[artifacts.length - 1] ??
      null,
    [activeArtifactKey, artifacts],
  );

  const finalAssistantMessage = useMemo(
    () => extractSearchWorkspaceAssistantText(streamedAssistantContent),
    [streamedAssistantContent],
  );

  const updateStage = useCallback(
    (id: SearchRunStage["id"], update: Partial<SearchRunStage>) => {
      setStages((previous) => {
        const targetIndex = previous.findIndex((stage) => stage.id === id);
        if (targetIndex < 0) return previous;
        return previous.map((stage, index) => {
          if (
            index < targetIndex &&
            stage.status !== "complete" &&
            stage.status !== "error"
          ) {
            return {
              ...stage,
              status: "complete",
              durationMs: stage.startedAt
                ? Date.now() - stage.startedAt
                : stage.durationMs,
            };
          }
          if (index !== targetIndex) return stage;
          return { ...stage, ...update };
        });
      });
    },
    [],
  );

  // 완료된 세션의 metadata.pipelineSteps(toolCalls 포함)에서 검색 단계 액션 복원
  const stagesFromMessages = useCallback(
    (
      messages: AgentSessionMessage[],
      forceComplete: boolean,
    ): SearchRunStage[] | null => {
      const assistant = [...messages]
        .reverse()
        .find(
          (message) =>
            message.role === "assistant" &&
            Array.isArray(message.metadata?.pipelineSteps),
        );
      const pipelineSteps = assistant?.metadata?.pipelineSteps;
      if (!Array.isArray(pipelineSteps) || pipelineSteps.length === 0)
        return null;
      const actions: SearchRunAction[] = [];
      let stepStatus: SearchRunStageStatus = forceComplete
        ? "complete"
        : "running";
      let durationMs: number | undefined;
      for (const value of pipelineSteps) {
        if (!value || typeof value !== "object") continue;
        const step = value as Record<string, unknown>;
        if (
          ["pending", "running", "complete", "error"].includes(
            String(step.status),
          ) &&
          !forceComplete
        ) {
          stepStatus = String(step.status) as SearchRunStageStatus;
        }
        if (Number.isFinite(Number(step.durationMs)))
          durationMs = Number(step.durationMs);
        const toolCalls = Array.isArray(step.toolCalls) ? step.toolCalls : [];
        for (const rawTool of toolCalls) {
          if (!rawTool || typeof rawTool !== "object") continue;
          const tool = rawTool as Record<string, unknown>;
          const toolName =
            typeof tool.toolName === "string" ? tool.toolName : "unknown";
          actions.push({
            id:
              typeof tool.toolCallId === "string"
                ? tool.toolCallId
                : `${toolName}-${actions.length}`,
            toolName,
            title: toolTitle(toolName),
            status: forceComplete
              ? "complete"
              : tool.status === "error"
                ? "error"
                : tool.status === "running"
                  ? "running"
                  : "complete",
          });
        }
      }
      return [
        { id: "verify", title: "검색 조건 확인", status: "complete" },
        {
          id: "search",
          title: config.searchStageTitle,
          status: forceComplete ? "complete" : stepStatus,
          actions: actions.length ? actions : undefined,
        },
        {
          id: "report",
          title: config.reportStageTitle,
          status: forceComplete
            ? "complete"
            : stepStatus === "complete"
              ? "complete"
              : "pending",
          durationMs,
        },
      ];
    },
    [config.reportStageTitle, config.searchStageTitle, toolTitle],
  );

  const refreshCanonicalSession = useCallback(
    async (targetSessionId: string) => {
      const messages = await loadAgentSessionMessages(targetSessionId);
      if (activeSessionIdRef.current !== targetSessionId) return;
      const nextArtifacts = extractSearchWorkspaceArtifacts(messages, config);
      const lastAssistant = [...messages]
        .reverse()
        .find((message) => message.role === "assistant");
      if (lastAssistant?.content)
        setStreamedAssistantContent(lastAssistant.content);
      if (nextArtifacts.length) {
        setArtifacts(nextArtifacts);
        setActiveArtifactKey(nextArtifacts[nextArtifacts.length - 1].key);
      }
    },
    [config],
  );

  const finishWithContent = useCallback(
    async (content: string, targetSessionId: string) => {
      setStreamedAssistantContent(content);
      if (/<ask_user(?:\s|>)/i.test(content)) {
        setNeedsInput(true);
        setError(
          xmlText("question", content) ||
            "검색을 진행하려면 추가 정보가 필요합니다.",
        );
        setState("failed");
        updateStage("search", {
          status: "error",
          summary: "입력 정보가 부족합니다.",
        });
        return;
      }
      const hasArtifact = new RegExp(
        `<${config.artifactTag}(?:\\s|>)`,
        "i",
      ).test(content);
      if (!hasArtifact && !extractSearchWorkspaceAssistantText(content)) {
        setError(
          "검색 결과를 확인하지 못했습니다. 조건을 보완해 다시 검색해주세요.",
        );
        setState("failed");
        updateStage("report", { status: "error" });
        return;
      }
      if (hasArtifact) {
        const streamedArtifacts = extractSearchWorkspaceArtifacts(
          [{ role: "assistant", content }],
          config,
        );
        if (streamedArtifacts.length) {
          setArtifacts(streamedArtifacts);
          setActiveArtifactKey(
            streamedArtifacts[streamedArtifacts.length - 1].key,
          );
        }
      }
      setStages((previous) =>
        previous.map((stage) => ({
          ...stage,
          status: "complete",
          durationMs: stage.startedAt
            ? Date.now() - stage.startedAt
            : stage.durationMs,
          actions: stage.actions?.map((action) =>
            action.status === "running"
              ? { ...action, status: "complete" as const }
              : action,
          ),
        })),
      );
      await refreshCanonicalSession(targetSessionId).catch(() => undefined);
      if (activeSessionIdRef.current === targetSessionId) setState("ready");
    },
    [config, refreshCanonicalSession, updateStage],
  );

  const applyRunEvent = useCallback(
    (event: Record<string, unknown>) => {
      const sequence = Number(event.sequence);
      if (Number.isSafeInteger(sequence)) {
        if (sequence <= lastSequenceRef.current) return { terminal: false };
        lastSequenceRef.current = sequence;
      }
      const type = String(event.type ?? "");
      if (type === "text-snapshot" && typeof event.text === "string") {
        runContentRef.current = event.text;
        setStreamedAssistantContent(event.text);
        if (
          new RegExp(`<${config.artifactTag}(?:\\s|>)`, "i").test(event.text)
        ) {
          updateStage("report", { status: "running", startedAt: Date.now() });
        }
      } else if (type === "agent-text-reset" && event.scope === "assistant") {
        runContentRef.current = "";
        setStreamedAssistantContent("");
      } else if (
        type === "run-completed" &&
        typeof event.content === "string"
      ) {
        runContentRef.current = event.content;
        setStreamedAssistantContent(event.content);
      } else if (type === "agent-start") {
        updateStage("search", { status: "running", startedAt: Date.now() });
      } else if (type === "tool-call-start") {
        const toolName =
          typeof event.toolName === "string" ? event.toolName : "unknown";
        const toolCallId =
          typeof event.toolCallId === "string"
            ? event.toolCallId
            : `${toolName}-${Date.now()}`;
        setStages((previous) =>
          previous.map((stage) =>
            stage.id !== "search"
              ? stage
              : {
                  ...stage,
                  status: "running",
                  startedAt: stage.startedAt ?? Date.now(),
                  actions: [
                    ...(stage.actions ?? []).filter(
                      (action) => action.id !== toolCallId,
                    ),
                    {
                      id: toolCallId,
                      toolName,
                      title: toolTitle(toolName),
                      status: "running" as const,
                      startedAt: Date.now(),
                      inputPreview: previewToolValue(event.args),
                    },
                  ],
                },
          ),
        );
      } else if (type === "tool-result") {
        const toolCallId =
          typeof event.toolCallId === "string" ? event.toolCallId : null;
        setStages((previous) =>
          previous.map((stage) =>
            stage.id !== "search"
              ? stage
              : {
                  ...stage,
                  actions: stage.actions?.map((action) =>
                    action.id !== toolCallId
                      ? action
                      : {
                          ...action,
                          status:
                            event.status === "error" ? "error" : "complete",
                          durationMs: action.startedAt
                            ? Date.now() - action.startedAt
                            : undefined,
                          resultPreview:
                            previewToolValue(event.resultText) ??
                            cleanSummary(event.summary, 400),
                        },
                  ),
                },
          ),
        );
      } else if (type === "agent-complete") {
        setStages((previous) =>
          previous.map((stage) =>
            stage.id === "verify"
              ? stage
              : {
                  ...stage,
                  status: stage.status === "error" ? stage.status : "complete",
                  durationMs:
                    stage.id === "report" &&
                    Number.isFinite(Number(event.durationMs))
                      ? Number(event.durationMs)
                      : stage.startedAt
                        ? Date.now() - stage.startedAt
                        : stage.durationMs,
                  actions: stage.actions?.map((action) =>
                    action.status === "running"
                      ? { ...action, status: "complete" as const }
                      : action,
                  ),
                },
          ),
        );
      } else if (type === "error") {
        setStages((previous) => {
          const runningIndex = previous.findIndex(
            (stage) => stage.status === "running",
          );
          const targetIndex = runningIndex >= 0 ? runningIndex : 1;
          return previous.map((stage, index) =>
            index === targetIndex
              ? {
                  ...stage,
                  status: "error",
                  summary: cleanSummary(event.message),
                }
              : stage,
          );
        });
      }
      return {
        terminal: ["run-completed", "run-failed", "run-cancelled"].includes(
          type,
        ),
        type,
      };
    },
    [config.artifactTag, toolTitle, updateStage],
  );

  const consumeRun = useCallback(
    async (targetRunId: string, targetSessionId: string, after = 0) => {
      streamAbortRef.current?.abort();
      const controller = new AbortController();
      streamAbortRef.current = controller;
      lastSequenceRef.current = after;
      let terminal = false;
      while (!controller.signal.aborted && !terminal) {
        try {
          const response = await openAgentRunEvents(
            targetRunId,
            lastSequenceRef.current,
            controller.signal,
          );
          if (!response.ok || !response.body)
            throw new Error(`이벤트 연결 실패 (${response.status})`);
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!controller.signal.aborted) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";
            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const raw = line.slice(6).trim();
              if (!raw || raw === "[DONE]") continue;
              const event = JSON.parse(raw) as Record<string, unknown>;
              const result = applyRunEvent(event);
              if (!result.terminal) continue;
              terminal = true;
              if (result.type === "run-completed") {
                await finishWithContent(runContentRef.current, targetSessionId);
              } else if (result.type === "run-cancelled") {
                setState("cancelled");
                setError("검색을 취소했습니다.");
              } else {
                setState("failed");
                setError(
                  cleanSummary(event.message) || "검색 중 오류가 발생했습니다.",
                );
              }
              break;
            }
            if (terminal) break;
          }
          if (!terminal && !controller.signal.aborted) {
            await new Promise((resolve) => window.setTimeout(resolve, 700));
          }
        } catch (caught) {
          if (controller.signal.aborted) break;
          await new Promise((resolve) => window.setTimeout(resolve, 900));
          if (activeSessionIdRef.current !== targetSessionId) break;
          if (caught instanceof Error && /401|403/.test(caught.message)) {
            setState("failed");
            setError("세션 인증을 확인할 수 없습니다.");
            break;
          }
        }
      }
      if (streamAbortRef.current === controller) streamAbortRef.current = null;
    },
    [applyRunEvent, finishWithContent],
  );

  const loadSession = useCallback(
    async (targetSessionId: string) => {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      activeSessionIdRef.current = targetSessionId;
      setSessionId(targetSessionId);
      setState("loading");
      setError(null);
      setNeedsInput(false);
      clearRunOutput();
      try {
        const messages = await loadAgentSessionMessages(targetSessionId);
        if (activeSessionIdRef.current !== targetSessionId) return;
        const nextArtifacts = extractSearchWorkspaceArtifacts(messages, config);
        const lastAssistant = [...messages]
          .reverse()
          .find((message) => message.role === "assistant");
        setStreamedAssistantContent(lastAssistant?.content ?? "");
        setArtifacts(nextArtifacts);
        const generatingMessage = [...messages]
          .reverse()
          .find(
            (message) =>
              message.role === "assistant" &&
              message.metadata?.is_generating &&
              message.metadata?.run_id,
          );
        if (generatingMessage) {
          const activeRunId = String(generatingMessage.metadata?.run_id);
          const after = Number(
            generatingMessage.metadata?.last_event_sequence ?? 0,
          );
          setRunId(activeRunId);
          setStages(
            stagesFromMessages(messages, false) ??
              initialStages.map((stage, index) => ({
                ...stage,
                status:
                  index === 0
                    ? "complete"
                    : index === 1
                      ? "running"
                      : "pending",
              })),
          );
          setState("generating");
          void consumeRun(activeRunId, targetSessionId, after);
          return;
        }
        if (nextArtifacts.length) {
          setActiveArtifactKey(nextArtifacts[nextArtifacts.length - 1].key);
          setStages(
            stagesFromMessages(messages, true) ??
              initialStages.map((stage) => ({ ...stage, status: "complete" })),
          );
          setState("ready");
          return;
        }
        if (lastAssistant && /<ask_user(?:\s|>)/i.test(lastAssistant.content)) {
          setNeedsInput(true);
          setError(
            xmlText("question", lastAssistant.content) ||
              "검색을 진행하려면 추가 정보가 필요합니다.",
          );
          setState("failed");
          return;
        }
        // 이전 채팅형 세션 등 아티팩트 없이 답변만 있는 세션도 열람 가능해야 한다.
        if (
          lastAssistant &&
          extractSearchWorkspaceAssistantText(lastAssistant.content)
        ) {
          setStages(
            stagesFromMessages(messages, true) ??
              initialStages.map((stage) => ({ ...stage, status: "complete" })),
          );
          setState("ready");
          return;
        }
        setError(
          `이 세션에서 ${config.resultNoun} 검색 결과를 찾지 못했습니다.`,
        );
        setState("failed");
      } catch (caught) {
        if (activeSessionIdRef.current !== targetSessionId) return;
        setState("failed");
        setError(
          caught instanceof Error
            ? caught.message
            : "세션을 불러오지 못했습니다.",
        );
      }
    },
    [clearRunOutput, config, consumeRun, initialStages, stagesFromMessages],
  );

  useEffect(() => {
    const suspendRunStream = () => {
      if (!streamAbortRef.current) return;
      streamLifecycleSuspendedRef.current = true;
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    };
    const resumeRunStream = () => {
      if (!streamLifecycleSuspendedRef.current) return;
      streamLifecycleSuspendedRef.current = false;
      const targetSessionId = activeSessionIdRef.current;
      if (targetSessionId) void loadSession(targetSessionId);
    };

    window.addEventListener(APP_SUSPEND_EVENT, suspendRunStream);
    window.addEventListener(APP_RESUME_EVENT, resumeRunStream);
    return () => {
      window.removeEventListener(APP_SUSPEND_EVENT, suspendRunStream);
      window.removeEventListener(APP_RESUME_EVENT, resumeRunStream);
    };
  }, [loadSession]);

  useEffect(() => {
    const isInitialSync = !hasSyncedInitialRouteRef.current;
    hasSyncedInitialRouteRef.current = true;
    const action = resolveRiskAssessmentRouteSyncAction({
      routeSessionId,
      activeSessionId: activeSessionIdRef.current,
      isInitialSync,
    });
    if (action === "load" && routeSessionId) {
      void loadSession(routeSessionId);
    } else if (action === "reset") {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      activeSessionIdRef.current = null;
      setSessionId(null);
      setRunId(null);
      setStages(initialStages);
      clearRunOutput();
      setError(null);
      setNeedsInput(false);
      setState("idle");
    }
  }, [clearRunOutput, initialStages, loadSession, routeSessionId]);

  useEffect(() => {
    let cancelled = false;
    void fetchAllProjects({ skipRedirectOn401: true })
      .then((fetched) => {
        if (cancelled) return;
        setProjects(
          fetched.map((project) => ({ id: project.id, name: project.name })),
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMoreSessions = useCallback(
    async (reset = false) => {
      if (sessionsLoading) return;
      setSessionsLoading(true);
      try {
        let page = reset ? 1 : sessionPageRef.current;
        const collected: AgentSessionListItem[] = [];
        let more = true;
        while (more && collected.length < 5) {
          const result = await listAgentSessions(
            page,
            SESSION_BATCH_SIZE,
            SESSION_SURFACE,
          );
          collected.push(
            ...result.sessions.filter(
              (session) => session.agent_id === config.agentId,
            ),
          );
          page += 1;
          more = result.page < result.totalPages;
        }
        sessionPageRef.current = page;
        setHasMoreSessions(more);
        setSessions((previous) => {
          const values = reset ? collected : [...previous, ...collected];
          return values.filter(
            (session, index, all) =>
              all.findIndex((candidate) => candidate.id === session.id) ===
              index,
          );
        });
      } catch {
        if (reset) setSessions([]);
      } finally {
        if (reset) setSessionsInitialized(true);
        setSessionsLoading(false);
      }
    },
    [config.agentId, sessionsLoading],
  );

  const loadAllSessions = useCallback(async () => {
    if (sessionsLoading || !hasMoreSessions) return;
    setSessionsLoading(true);
    try {
      let page = sessionPageRef.current;
      const collected: AgentSessionListItem[] = [];
      let more = true;
      while (more) {
        const result = await listAgentSessions(
          page,
          SESSION_BATCH_SIZE,
          SESSION_SURFACE,
        );
        collected.push(
          ...result.sessions.filter(
            (session) => session.agent_id === config.agentId,
          ),
        );
        page += 1;
        more = result.page < result.totalPages;
      }
      sessionPageRef.current = page;
      setHasMoreSessions(false);
      setSessions((previous) =>
        [...previous, ...collected].filter(
          (session, index, all) =>
            all.findIndex((candidate) => candidate.id === session.id) === index,
        ),
      );
    } finally {
      setSessionsLoading(false);
    }
  }, [config.agentId, hasMoreSessions, sessionsLoading]);

  useEffect(() => {
    void loadMoreSessions(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useCallback(
    async (submission: SearchWorkspaceSubmission) => {
      if (!["idle", "failed", "cancelled"].includes(state)) return;
      setError(null);
      setNeedsInput(false);
      clearRunOutput();
      setStages(
        initialStages.map((stage, index) => ({
          ...stage,
          status: index === 0 ? "running" : "pending",
          startedAt: index === 0 ? Date.now() : undefined,
        })),
      );
      setState("generating");
      let createdSessionId: string | null = null;
      try {
        createdSessionId = await createAgentSession({
          agentId: config.agentId,
          projectId: submission.projectId,
          surface: SESSION_SURFACE,
        });
        activeSessionIdRef.current = createdSessionId;
        setSessionId(createdSessionId);
        const deviceContext = await getDeviceContextSnapshot();
        const payload = buildAgentRunPayload({
          message: submission.prompt,
          sessionId: createdSessionId,
          userLanguage: languageLabel,
          modelMode: submission.modelMode,
          reasoningStyle,
          deviceContext,
        });
        const controller = new AbortController();
        streamAbortRef.current = controller;
        const createdRunId = await createAgentRun(
          config.agentId,
          payload,
          controller.signal,
        );
        setRunId(createdRunId);
        lastSequenceRef.current = 0;
        updateStage("verify", {
          status: "complete",
          durationMs: 0,
          summary: "검색 조건을 안전하게 전달했습니다.",
        });
        updateStage("search", { status: "running", startedAt: Date.now() });
        // Core가 durable run을 만든 뒤에만 URL을 공개한다 — 라우트 재마운트 시
        // 새 인스턴스가 생성 중인 메시지에 재접속할 수 있게 하기 위함.
        window.history.replaceState(
          null,
          "",
          getAgentSessionHref(config.agentId, createdSessionId),
        );
        window.dispatchEvent(new Event("session-created"));
        void consumeRun(createdRunId, createdSessionId, 0);
        void loadMoreSessions(true);
      } catch (caught) {
        setState("failed");
        setError(
          caught instanceof Error
            ? caught.message
            : "검색을 시작하지 못했습니다.",
        );
        updateStage("verify", { status: "error" });
        if (!createdSessionId) activeSessionIdRef.current = null;
      }
    },
    [
      clearRunOutput,
      config.agentId,
      consumeRun,
      initialStages,
      languageLabel,
      reasoningStyle,
      loadMoreSessions,
      state,
      updateStage,
    ],
  );

  const cancel = useCallback(async () => {
    if (!runId) return;
    await cancelAgentRun(runId).catch(() => undefined);
    streamAbortRef.current?.abort();
    setState("cancelled");
    setError("검색을 취소했습니다.");
  }, [runId]);

  const reset = useCallback(() => {
    streamAbortRef.current?.abort();
    activeSessionIdRef.current = null;
    setSessionId(null);
    setRunId(null);
    setStages(initialStages);
    clearRunOutput();
    setError(null);
    setNeedsInput(false);
    setState("idle");
    window.history.replaceState(null, "", getAgentPath(config.agentId));
  }, [clearRunOutput, config.agentId, initialStages]);

  const saveArtifact = useCallback(
    async (xmlContent: string, title: string) => {
      if (!activeArtifact || !sessionId || !activeArtifact.messageId) return;
      const replacement = `<${config.artifactTag}>\n${xmlContent}\n</${config.artifactTag}>`;
      const nextContent = activeArtifact.fullContent.replace(
        new RegExp(
          `<${config.artifactTag}(?:\\s+[^>]*)?>[\\s\\S]*?(?:<\\/${config.artifactTag}>|$)`,
          "i",
        ),
        replacement,
      );
      const response = await fetchWithAuth(
        `${apiUrl()}/api/chat/sessions/${sessionId}/messages/${activeArtifact.messageId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: nextContent }),
          skipRedirectOn401: false,
        },
      );
      if (!response.ok) throw new Error("검색 결과를 저장하지 못했습니다.");
      await fetchWithAuth(`${apiUrl()}/api/artifacts/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          type: config.artifactTag,
          content: xmlContent,
          session_id: sessionId,
          message_id: activeArtifact.messageId,
        }),
        skipRedirectOn401: true,
      }).catch(() => undefined);
      setArtifacts((previous) =>
        previous.map((artifact) =>
          artifact.key === activeArtifact.key
            ? { ...artifact, title, xmlContent, fullContent: nextContent }
            : artifact,
        ),
      );
    },
    [activeArtifact, config.artifactTag, sessionId],
  );

  useEffect(
    () => () => {
      streamAbortRef.current?.abort();
    },
    [],
  );

  return {
    state,
    sessionId,
    runId,
    stages,
    artifacts,
    activeArtifact,
    activeArtifactKey,
    setActiveArtifactKey,
    streamedAssistantContent,
    finalAssistantMessage,
    projects,
    sessions,
    sessionsLoading,
    sessionsInitialized,
    hasMoreSessions,
    loadMoreSessions: () => loadMoreSessions(false),
    loadAllSessions,
    loadSession,
    error,
    needsInput,
    submit,
    cancel,
    reset,
    saveArtifact,
  };
}
